/**
 * §24.3 — `can`/`canAny`, the client-side permission gate `usePermission()`
 * hands to every screen that calls it.
 *
 * THIS FILE USED TO ALSO EXPORT A HAND-MAINTAINED MIRROR of the backend's
 * permission catalogue (`PERMISSIONS`, `ALL_PERMISSION_KEYS`,
 * `PermissionResource`), claiming to be "key for key and in the same order"
 * as `backend/src/common/constants/permissions.ts` and asserted deep-equal by
 * `test/permissions.test.ts`. Neither claim was true: the mirror had drifted
 * to 29 of the backend's 71 families, and `test/permissions.test.ts` never
 * existed on any branch — `git log --all --diff-filter=D` finds no trace of
 * it. The incident that header invoked to justify itself was real: M2/D-05,
 * the dashboard gating its branch picker on `settings.view` for months
 * because `branches.view` had never reached the mirror. But nothing here ever
 * imported the mirror either — `grep -rn "domain/permissions" src/` outside
 * this file returned nothing for the catalogue export, and the one screen
 * that most plausibly needed the full permission list, the RBAC role editor
 * (`Pages/Settings/components/PermissionMatrixDialog.tsx`), has always
 * fetched it live from `GET /permissions` rather than from here. So a
 * complete, current mirror would not have caught D-05 or anything since — it
 * was decoration, not a guard, in a codebase whose whole philosophy is that
 * the server is authoritative and the client re-fetches (see `can`/`canAny`'s
 * own doc comment below).
 *
 * The catalogue data now lives at
 * `src/lib/domain/permission-catalogue.generated.json`, generated from the
 * backend by `scripts/generate-permission-keys.mjs` (run by hand — the two
 * repos do not sit together in CI, see that script for why). It is NOT
 * re-exported from here: nothing at runtime needs it, and re-exporting an
 * unused catalogue "because it's generated now" would just be the same dead
 * weight with a different excuse. Its one real consumer is
 * `eslint-rules/permission-key-exists.js`, which imports it directly and
 * fails the build when a screen gates on a permission string that is not in
 * the snapshot — a typo, or a key removed on the backend, which fails a gate
 * closed and hides a feature from everyone. Read that rule's header for
 * exactly what it does and does not catch: notably, it would NOT have caught
 * D-05 as it actually happened, because `settings.view` is a perfectly valid
 * key, just the wrong one for a branch picker — see the comments at
 * `Layout.tsx`'s and `Pages/Operations/shared.ts`'s `can('branches.view')`
 * call sites, which name that directly. `scripts/audit-permission-coverage.mjs`
 * covers the opposite direction — a permission the backend grants that no
 * screen gates on at all, which is how `scheduling.view_own` and
 * `scheduling.swap_request` shipped unreachable for every floor role that
 * held them. It is a manually-run report, not a CI gate; see its header for
 * why.
 */

/** Client-side gate. The server re-checks everything — this only hides UI. */
export function can(
  user: { permissions: string[]; isPlatformAdmin?: boolean } | null | undefined,
  ...required: string[]
): boolean {
  if (!user) return false;
  if (user.isPlatformAdmin) return true;
  return required.every((key) => user.permissions.includes(key));
}

export function canAny(
  user: { permissions: string[]; isPlatformAdmin?: boolean } | null | undefined,
  ...required: string[]
): boolean {
  if (!user) return false;
  if (user.isPlatformAdmin) return true;
  return required.some((key) => user.permissions.includes(key));
}
